// @catalog F.006
// KPICardsVentas — 4 KPIs ejecutivos de /ventas (plan dashboard_executive_redesign).
//   1. Ventas del mes (current.salesAmount) + delta YoY + sparkline 12m
//   2. Proyección cierre (current.projectedSales) + pill %mes + ProjectionBar 30d
//   3. YTD acumulado + delta vs YTD año anterior + sparkline meses YTD
//   4. Ticket promedio (sales/orders) + delta vs LY + sparkline 12m
import { useMemo } from "react";
import { Skeleton, Stack } from "@mui/material";

import AnalyticsService, {
  type MonthlyMetrics,
  type StoreMetrics,
} from "../../services/analytics.service";
import { KPICard } from "../Dashboard/KPICard";
import { ProjectionBar } from "../analytics/ProjectionBar";
import {
  buildSparkline,
  computeAvgTicket,
  computeAvgTicketLY,
  computePeriodPrevYear,
  computePeriodTotal,
  monthNameToIndex,
  rangeStartsInJanuary,
} from "./helpers";
import { TOOLTIP_VENTAS_COBRADAS } from "../../constants/dashboard-tooltips";

interface KPICardsVentasProps {
  monthlyData: MonthlyMetrics[] | undefined;
  // topStores se conserva en la firma por backward-compat (los consumers la
  // pasan), pero ya no se usa para "Mejor tienda" — ese KPI se relegó al
  // RankingBars del cuerpo.
  topStores?: StoreMetrics[] | undefined;
  loading: boolean;
}

function pickCurrentMonth(
  data: MonthlyMetrics[],
): { current: MonthlyMetrics | null; previous: MonthlyMetrics | null } {
  if (data.length === 0) return { current: null, previous: null };
  const sorted = [...data].sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return monthNameToIndex(a.month) - monthNameToIndex(b.month);
  });
  const current = sorted[sorted.length - 1] ?? null;
  const previous = sorted.length > 1 ? sorted[sorted.length - 2] ?? null : null;
  return { current, previous };
}

function computeYoYDelta(current: MonthlyMetrics | null): number | null {
  if (!current) return null;
  if (current.growthPercentage !== undefined && current.growthPercentage !== null) {
    return current.growthPercentage;
  }
  if (
    current.previousYearSalesAmount === undefined ||
    current.previousYearSalesAmount === 0
  ) {
    return null;
  }
  return (
    ((current.salesAmount - current.previousYearSalesAmount) /
      current.previousYearSalesAmount) *
    100
  );
}

function partialPct(m: MonthlyMetrics | null): number | null {
  if (
    !m ||
    !m.isCurrentMonth ||
    m.daysElapsed === undefined ||
    m.daysInMonth === undefined ||
    m.daysInMonth === 0
  ) {
    return null;
  }
  return (m.daysElapsed / m.daysInMonth) * 100;
}

export function KPICardsVentas(props: KPICardsVentasProps): JSX.Element {
  const { monthlyData, loading } = props;

  const computed = useMemo(() => {
    const data = monthlyData ?? [];
    const { current } = pickCurrentMonth(data);
    if (!current) {
      return {
        current: null,
        sparklineSales: [] as Array<{ value: number }>,
        sparklineTicket: [] as Array<{ value: number }>,
        sparklinePeriod: [] as Array<{ value: number }>,
        periodTotal: 0,
        periodPrev: 0,
        startsInJanuary: false,
        avgTicket: null as number | null,
        avgTicketLY: null as number | null,
      };
    }
    return {
      current,
      sparklineSales: buildSparkline(data, (m) => m.salesAmount, 12),
      sparklineTicket: buildSparkline(
        data,
        (m) => (m.ordersCount > 0 ? m.salesAmount / m.ordersCount : 0),
        12,
      ),
      // F4: el sparkline del KPI de período refleja todos los meses del rango.
      sparklinePeriod: buildSparkline(data, (m) => m.salesAmount, 12),
      periodTotal: computePeriodTotal(data),
      periodPrev: computePeriodPrevYear(data),
      startsInJanuary: rangeStartsInJanuary(data),
      avgTicket: computeAvgTicket(current),
      avgTicketLY: computeAvgTicketLY(current),
    };
  }, [monthlyData]);

  if (loading) {
    return (
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={160}
            sx={{ flex: 1, borderRadius: 1 }}
          />
        ))}
      </Stack>
    );
  }

  const {
    current,
    sparklineSales,
    sparklineTicket,
    sparklinePeriod,
    periodTotal,
    periodPrev,
    startsInJanuary,
    avgTicket,
    avgTicketLY,
  } = computed;

  const yoyDelta = computeYoYDelta(current);
  const partial = partialPct(current);
  const periodDelta =
    periodPrev > 0 ? ((periodTotal - periodPrev) / periodPrev) * 100 : null;
  const ticketDelta =
    avgTicket && avgTicketLY && avgTicketLY > 0
      ? ((avgTicket - avgTicketLY) / avgTicketLY) * 100
      : null;

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      sx={{ width: "100%", flexWrap: "wrap" }}
      useFlexGap
    >
      <KPICard
        accent
        title="Ventas del mes"
        value={
          current ? AnalyticsService.formatCurrency(current.salesAmount) : "—"
        }
        deltaPercent={yoyDelta}
        deltaLabel={
          current && current.previousYearSalesAmount !== undefined
            ? `vs. ${current.year - 1}`
            : "Sin base"
        }
        helperText={
          current && current.isCurrentMonth && current.lastDataDate
            ? `Datos hasta ${current.lastDataDate}`
            : current
              ? `${capitalize(current.month)} ${current.year}`
              : undefined
        }
        sparklineData={sparklineSales}
        tooltip={TOOLTIP_VENTAS_COBRADAS}
      />

      <KPICard
        title="Proyección cierre"
        value={
          current && current.projectedSales !== undefined
            ? AnalyticsService.formatCurrency(current.projectedSales)
            : current
              ? AnalyticsService.formatCurrency(current.salesAmount)
              : "—"
        }
        helperText={
          current &&
          current.daysElapsed !== undefined &&
          current.daysInMonth !== undefined
            ? `Lineal sobre ${current.daysElapsed}d`
            : "Sin proyección"
        }
        pill={
          partial !== null
            ? {
                label: `${Math.round(partial)}% del mes`,
                tone: "neutral",
              }
            : undefined
        }
        visualSlot={
          current &&
          current.daysElapsed !== undefined &&
          current.daysInMonth !== undefined ? (
            <ProjectionBar
              daysElapsed={current.daysElapsed}
              daysInMonth={current.daysInMonth}
            />
          ) : undefined
        }
        tooltip="Proyección lineal del cierre del mes basada en los días con datos sobre el total de días del mes."
      />

      <KPICard
        title={startsInJanuary ? "YTD acumulado" : "Total del período"}
        value={AnalyticsService.formatCurrency(periodTotal)}
        deltaPercent={periodDelta}
        deltaLabel={current ? `vs. ${current.year - 1}` : "Sin base"}
        sparklineData={sparklinePeriod}
        tooltip={
          startsInJanuary
            ? "Year-to-date: suma de ventas del año en curso desde enero hasta el último mes del rango."
            : "Suma de ventas de todos los meses del período seleccionado."
        }
      />

      <KPICard
        title="Ticket promedio"
        value={
          avgTicket !== null ? AnalyticsService.formatCurrency(avgTicket) : "—"
        }
        deltaPercent={ticketDelta}
        deltaLabel={
          current
            ? `${AnalyticsService.formatNumber(current.ordersCount)} órdenes`
            : ""
        }
        sparklineData={sparklineTicket}
        tooltip="Ticket promedio = ventas / órdenes del mes."
      />
    </Stack>
  );
}

function capitalize(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}
